import { useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowRight, Users } from "lucide-react";
import { EditorialPage, handwritten } from "../components/editorial";
import Button from "../components/Button";

/**
 * /people/<slug> — placeholder during the Happenings pivot.
 *
 * The slug is captured but not used. The previous version of this route
 * served founder networking landings for IRL business cards (LL-0060,
 * LL-0058, LL-0036). That product surface is paused while we focus on
 * Happenings. The route is kept live so any existing card / backlink
 * does not 404 — it gracefully directs the visitor to the main site
 * and the events surface.
 *
 * When the native app ships host profiles (Phase 2 of the app build),
 * this route will be repurposed as the canonical web profile for each
 * host (e.g. /people/sophie-walker → Sophie's host history + upcoming
 * Happenings + loop history).
 */
export default function PeoplePage() {
  const { slug = "" } = useParams<{ slug: string }>();

  useEffect(() => {
    document.title = "Profile | LetsLoop";
  }, [slug]);

  return (
    <EditorialPage className="flex items-center justify-center px-4 py-32">
      <div className="relative max-w-2xl w-full text-center">
        <div className="flex items-center justify-center gap-4 mb-6">
          <span className="text-xs font-bold uppercase tracking-[0.25em] text-salmon">
            Profiles
          </span>
        </div>
        <p className="text-3xl text-salmon -rotate-1 mb-3" style={handwritten}>
          coming back soon
        </p>
        <h1 className="text-4xl md:text-6xl font-serif tracking-tight text-ink mb-6 leading-[1.05]">
          Profile pages return
          <br />
          <span className="italic text-salmon">with the app.</span>
        </h1>
        <p className="text-lg text-gray-700 leading-relaxed mb-8 max-w-xl mx-auto">
          We&rsquo;re rebuilding LetsLoop as a spontaneous community network for
          the things you do anyway. Individual profile pages will return as host
          pages once the new app ships: every host&rsquo;s loops, attendance
          history, and people they&rsquo;ve looped with, in one place.
        </p>
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
          <Link to="/how-it-works">
            <Button variant="primary" size="lg">
              See how it works
              <ArrowRight size={18} className="ml-2 inline" />
            </Button>
          </Link>
          <Link
            to="/download"
            className="text-ink/70 font-semibold border-b-2 border-ink/20 hover:border-salmon hover:text-salmon transition-colors text-base inline-flex items-center gap-1.5"
          >
            <Users size={16} />
            See loops in the app &rarr;
          </Link>
        </div>
      </div>
    </EditorialPage>
  );
}
